/**
 * @fileoverview Event type definitions for screen-view-v2 package
 * @module screen-view-v2/types/events
 *
 * Implements:
 * - FR-062: Package emits custom events for user interactions
 * - FR-063: Events include full context (action, step, element, eventType)
 * - FR-064: 8 event types (ButtonClick, ListItemSelect, SliderCycleComplete, etc.)
 * - FR-065: Events bubble via callback props (OnActionCallback, OnEventCallback)
 */

import type { StepNode } from '@segmently/schema-models/v2';

/**
 * Event types for all user interactions in the screen-view-v2 package.
 * FR-064: Defines the 8 standard event types.
 */
export enum EventType {
  /** User clicked a button */
  ButtonClick = 'ButtonClick',
  /** User selected a list item */
  ListItemSelect = 'ListItemSelect',
  /** User deselected a list item */
  ListItemDeselect = 'ListItemDeselect',
  /** User changed a field value */
  FieldChange = 'FieldChange',
  /** User moved the slider */
  SliderMove = 'SliderMove',
  /** Slider completed a full cycle (all slides shown) */
  SliderCycleComplete = 'SliderCycleComplete',
  /** User changed picker selection */
  PickerChange = 'PickerChange',
  /** User selected a calendar date */
  CalendarSelect = 'CalendarSelect',
}

/**
 * Event payload structure for all step events.
 * FR-063: Contains full context for event handling.
 *
 * @example
 * const event: StepEvent = {
 *   action: 'navigate',
 *   step: currentStep,
 *   element: 'footer-button-1',
 *   eventType: EventType.ButtonClick
 * };
 */
export interface StepEvent {
  /** Action identifier (e.g., "navigate", "subscribe", "skip") */
  action: string;
  /** Full step object that contains the element that triggered the event */
  step: StepNode;
  /** Optional ID of the element within the step that triggered the event */
  element?: string;
  /** Type of user interaction that occurred */
  eventType: EventType;
}

/**
 * Callback type for handling user actions.
 * FR-065: Used as prop type for components to bubble events up.
 *
 * @example
 * const handleAction: OnActionCallback = (event) => {
 *   console.log(`Action: ${event.action} on step ${event.step.id}`);
 * };
 */
export type OnActionCallback = (event: StepEvent) => void;

/**
 * Generic callback type for custom event handling.
 * FR-065: Alternative to OnActionCallback for more generic use cases.
 *
 * @example
 * const handleEvent: OnEventCallback = (event) => {
 *   if (event.eventType === EventType.ListItemSelect) {
 *     // Handle list selection
 *   }
 * };
 */
export type OnEventCallback = (event: StepEvent) => void;
